import { ContactInfo } from '@mdvip/shared/features/contact/ContactInfo';
import { useTranslation } from 'react-i18next';

/**
 * ContactPage: the Help & Support page's Contact Info content — MDVIP's own support phone/hours,
 * HQ mailing address, and a CONTACT US action. Shared (not app-local): legacy confirms this
 * exact page is reachable by both member and physician/staff sessions (SiteHeader.php's nav,
 * ContactUs.php's parallel physician_settings), following the repo's existing shared-page
 * precedent (LoginPage.tsx/AuthCallbackPage.tsx). Renders inside its app's AppLayout, so it owns
 * no chrome of its own — just the page head and ContactInfo, matching every other page's
 * content-only shape.
 *
 * Only mounted via <Route> in apps/members today — apps/providers has no Help & Support
 * nav/footer entry point yet, so a route there would have nothing linking to it. That's a
 * separate, not-yet-filed follow-up story, not deferred by oversight.
 *
 * `onOpenContact` is required rather than optional: this page's whole action is CONTACT US, and an
 * absent handler would ship a button that silently does nothing. The route element owns the modal
 * and its member-session prefill, which is what keeps this page role-agnostic and shared.
 */
export interface ContactPageProps {
  /** Opens the Contact Us modal. Supplied by the route element, which owns that modal's state. */
  onOpenContact: () => void;
}

export function ContactPage({ onOpenContact }: ContactPageProps) {
  const { t } = useTranslation('shared');

  return (
    <>
      <div className="mb-8">
        <h1 className="text-h1 font-semibold">{t('contact.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('contact.description')}</p>
      </div>
      <ContactInfo onOpenContact={onOpenContact} />
    </>
  );
}
